import { createSelector, PayloadAction } from '@reduxjs/toolkit';
import { Resizable } from 're-resizable';
import * as React from 'react';
import { default as Modal } from 'react-bootstrap/Modal';
import { GlobalHotKeys } from 'react-hotkeys';
import { WithTranslation, withTranslation } from 'react-i18next';

import { DataViewerPropagateState } from '../dtale/DataViewerState';
import { buildRangeState } from '../dtale/rangeSelectUtils';
import { AppActions } from '../redux/actions/AppActions';
import { closeChart } from '../redux/actions/charts';
import { loadDatasets } from '../redux/actions/merge';
import { useAppDispatch, useAppSelector, useMergeDispatch } from '../redux/hooks';
import { selectChartData, selectDataId } from '../redux/selectors';
import { RangeState } from '../redux/state/AppState';

import DraggableModalDialog from './DraggableModalDialog';
import * as popupUtils from './popupUtils';

/** re-resizable directions */
type Direction = 'top' | 'right' | 'bottom' | 'left' | 'topRight' | 'bottomRight' | 'bottomLeft' | 'topLeft';

/** Component properties for XArrayDimensions */
interface PopupProps {
  propagateState: DataViewerPropagateState;
}

const selectResult = createSelector([selectDataId, selectChartData], (dataId, chartData) => ({ chartData, dataId }));

const Popup: React.FC<PopupProps & WithTranslation> = ({ t, ...props }) => {
  const { chartData, dataId } = useAppSelector(selectResult);
  const dispatch = useAppDispatch();
  const mergeDispatch = useMergeDispatch();
  const mergeRefresher = async (): Promise<void> => await loadDatasets(mergeDispatch);
  const updateRangeState = (state: RangeState): PayloadAction<RangeState> =>
    dispatch(AppActions.SetRangeStateAction({ ...state }));

  const [minHeight, setMinHeight] = React.useState<number>();
  const [minWidth, setMinWidth] = React.useState<number>();

  React.useEffect(() => {
    if (!chartData.visible) {
      setMinHeight(undefined);
      setMinWidth(undefined);
    }
  }, []);

  React.useEffect(() => {
    if (!chartData.visible) {
      setMinHeight(undefined);
      setMinWidth(undefined);
    }
  }, [chartData]);

  const { type, visible, size, backdrop } = chartData;
  const onClose = (): void => {
    updateRangeState(buildRangeState());
    dispatch(closeChart());
  };
  const { title, body } = popupUtils.buildBodyAndTitle({
    ...props,
    dataId: dataId ?? '1',
    chartData,
    mergeRefresher,
    t,
  });

  const onResizeStart = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
    dir: Direction,
    elementRef: HTMLElement,
  ): void => {
    setMinHeight(elementRef.offsetHeight);
    setMinWidth(elementRef.offsetWidth);
  };

  return (
    <Modal
      {...{
        show: visible,
        onHide: onClose,
        dialogAs: DraggableModalDialog,
        size: size || 'lg',
        backdrop: backdrop ?? 'static',
        dialogClassName: `${type}-modal`,
      }}
      className={chartData.type}
    >
      {visible && <GlobalHotKeys keyMap={{ CLOSE_MODAL: 'esc' }} handlers={{ CLOSE_MODAL: onClose }} />}
      <Resizable
        className="modal-resizable"
        defaultSize={{ width: 'auto', height: 'auto' }}
        minHeight={minHeight}
        minWidth={minWidth}
        onResizeStart={onResizeStart}
      >
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
          <i className="ico-close pointer" onClick={onClose} />
        </Modal.Header>
        {visible && body}
        <span className="resizable-handle" />
      </Resizable>
    </Modal>
  );
};

export default withTranslation(['popup', 'filter', 'menu', 'column_menu'])(Popup);
